import { useMemo } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, CheckCheck, Compass, Loader2 } from "lucide-react";
import { ApiClientError } from "@/lib/api-client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  JOURNEY_EXECUTION_ORDER_LABEL,
  type JourneyExecutionOrder,
} from "@colab/shared";
import { formatRelative } from "@/lib/utils";
import { useJourneyInstanceDetail } from "./use-journey-instance-detail";
import { useCompleteJourney } from "./use-complete-journey";
import { JourneyProgressBar } from "./journey-progress-bar";
import { TaskCard } from "./task-card";

// /portal/journeys/:assignmentId — marquee page of sub-bucket 4. Header +
// progress bar + ordered task cards (one per progress row, sorted by
// task.position from the service). Discriminated affordances per
// task.action.kind via TaskCard.
//
// Mark-journey-complete button is exposed when every task is complete OR
// the assignment carries an existing completed_at (re-firing is idempotent).
// Service sets journey_assignments.completed_at and the list page's
// "Completed" tab populates on next visit.

export function JourneyDetailPage() {
  const { assignmentId = "" } = useParams<{ assignmentId: string }>();
  const detail = useJourneyInstanceDetail(assignmentId);
  const completeJourney = useCompleteJourney();

  if (detail.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    const notFound =
      detail.error instanceof ApiClientError && detail.error.status === 404;
    return (
      <div className="mx-auto max-w-2xl rounded-xl border bg-card p-8 text-center">
        <h2 className="font-display text-xl font-semibold tracking-tight">
          {notFound ? "Journey not found" : "Couldn’t load this journey"}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {notFound
            ? "It may have been removed, or you may not have access. Browse your other journeys instead."
            : "Please refresh the page. If the problem persists, contact support."}
        </p>
        <Link
          to="/portal/journeys"
          className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to journeys
        </Link>
      </div>
    );
  }

  const data = detail.data;
  return <JourneyDetailBody assignmentId={assignmentId} data={data} completing={completeJourney.isPending} onComplete={() => completeJourney.mutate({ assignmentId })} />;
}

function JourneyDetailBody({
  assignmentId,
  data,
  completing,
  onComplete,
}: {
  assignmentId: string;
  data: NonNullable<ReturnType<typeof useJourneyInstanceDetail>["data"]>;
  completing: boolean;
  onComplete: () => void;
}) {
  const { progress, journey, assignment } = data;
  const total = progress.length;
  const done = useMemo(
    () => progress.filter((p) => p.status === "completed").length,
    [progress],
  );
  const allDone = total > 0 && done >= total;
  const isComplete = assignment.completed_at !== null;

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to="/portal/journeys"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to journeys
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              {journey.name}
            </h1>
            <Badge variant="secondary" className="font-normal">
              {
                JOURNEY_EXECUTION_ORDER_LABEL[
                  journey.execution_order as JourneyExecutionOrder
                ]
              }
            </Badge>
            {isComplete ? (
              <Badge
                variant="outline"
                className="border-status-active/40 bg-status-active/10 text-status-active"
              >
                Completed
              </Badge>
            ) : null}
          </div>
          {journey.description ? (
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {journey.description}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground">
            Assigned {formatRelative(assignment.assigned_at)}
            {assignment.completed_at
              ? ` · Completed ${formatRelative(assignment.completed_at)}`
              : ""}
          </p>
        </div>

        {allDone && !isComplete ? (
          <Button
            type="button"
            onClick={onComplete}
            disabled={completing}
            className="shrink-0"
          >
            {completing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCheck className="mr-2 h-4 w-4" />
            )}
            Mark journey complete
          </Button>
        ) : null}
      </div>

      {total > 0 ? (
        <div className="mb-6 rounded-xl border bg-card p-5">
          <JourneyProgressBar done={done} total={total} />
        </div>
      ) : null}

      {progress.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-card p-10 text-center">
          <Compass className="mx-auto h-6 w-6 text-muted-foreground" />
          <h3 className="mt-3 font-display text-base font-semibold tracking-tight">
            No tasks yet
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            This journey doesn’t have any partner-visible tasks. Your
            partner manager may still be configuring it.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {progress.map((p) => (
            <TaskCard
              key={p.id}
              progress={p}
              assignmentId={assignmentId}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
